import { useEffect, useRef } from "react";
import LAND from "./land";

// ───────────────────────────────────────────────────────────────
// Globe — the mark's other face. Hidden at rest; when a project has a
// `route`, the sphere dissolves into a 1-bit Earth, turns and leans
// in, and the route draws itself across it a cell at a time.
// Same 4×4 Bayer as the shader, so the two read as one object.
// ───────────────────────────────────────────────────────────────

const CELLS = 28;
const R = Math.PI / 180;
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
const threshold = (i, j) => (BAYER[(j & 3) * 4 + (i & 3)] + 0.5) / 16;
const LIGHT = (() => {
  const v = [-0.55, 0.5, 0.7];
  const l = Math.hypot(...v);
  return v.map((x) => x / l);
})();

const bits = Uint8Array.from(atob(LAND), (c) => c.charCodeAt(0));
function isLand(lat, lon) {
  const x = Math.floor((((lon / R + 180) % 360) + 360) % 360);
  const y = Math.min(179, Math.max(0, Math.floor(90 - lat / R)));
  const i = y * 360 + x;
  return (bits[i >> 3] >> (i & 7)) & 1;
}

// points along great circles between waypoints, ~`per` per degree
function path(stops, per = 3) {
  const vec = ([lat, lon]) => [Math.cos(lat * R) * Math.cos(lon * R), Math.cos(lat * R) * Math.sin(lon * R), Math.sin(lat * R)];
  const out = [];
  for (let k = 0; k < stops.length - 1; k++) {
    const a = vec(stops[k]);
    const b = vec(stops[k + 1]);
    const w = Math.acos(Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
    const n = Math.max(2, Math.round((w / R) * per));
    for (let s = 0; s < n; s++) {
      const t = s / n;
      const f1 = Math.sin((1 - t) * w) / Math.sin(w);
      const f2 = Math.sin(t * w) / Math.sin(w);
      const p = a.map((v, i) => v * f1 + b[i] * f2);
      out.push({ lat: Math.asin(p[2]), lon: Math.atan2(p[1], p[0]) });
    }
  }
  const [lat, lon] = stops[stops.length - 1];
  out.push({ lat: lat * R, lon: lon * R });
  return out;
}

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export default function Globe({ route, color, size = 56 }) {
  const ref = useRef(null);
  const colorRef = useRef(color);
  colorRef.current = color;
  const run = useRef(null); // the route on show, and when it started / started leaving
  const raf = useRef(0);

  useEffect(() => {
    const cv = ref.current;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(size * dpr);
    cv.height = Math.round(size * dpr);
  }, [size]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  useEffect(() => {
    if (!route) {
      if (run.current && !run.current.out) run.current.out = performance.now();
      return;
    }
    const trail = path(route);
    // frame the whole route: centre on its middle, a little north
    const mid = trail[Math.floor(trail.length / 2)];
    run.current = { trail, lat0: mid.lat + 4 * R, lon0: mid.lon, start: performance.now(), out: 0 };
    cancelAnimationFrame(raf.current);

    const cv = ref.current;
    const ctx = cv.getContext("2d");
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const frame = (now) => {
      const r = run.current;
      const px = cv.width / CELLS;
      const t = still ? 1e9 : now - r.start;
      const leaving = r.out ? (still ? 1 : clamp01((now - r.out) / 300)) : 0;
      if (leaving >= 1) {
        ctx.clearRect(0, 0, cv.width, cv.height);
        run.current = null;
        return;
      }
      const shown = Math.min(clamp01(t / 350), 1 - leaving); // dissolve in / out
      const lean = ease(clamp01((t - 150) / 1000)); // turn and zoom in
      const ride = clamp01((t - 900) / 2400); // route draws
      const zoom = 1.1 + 1.7 * lean;
      const lon = r.lon0 - 45 * R * (1 - lean);
      const sl = Math.sin(r.lat0);
      const cl = Math.cos(r.lat0);

      const grid = new Uint8Array(CELLS * CELLS);
      for (let j = 0; j < CELLS; j++) {
        for (let i = 0; i < CELLS; i++) {
          const x0 = ((i + 0.5) / CELLS) * 2 - 1;
          const y0 = 1 - ((j + 0.5) / CELLS) * 2;
          if (x0 * x0 + y0 * y0 > 1) continue;
          const x = x0 / zoom;
          const y = y0 / zoom;
          const z = Math.sqrt(1 - x * x - y * y);
          const la = Math.asin(z * sl + y * cl);
          const lo = lon + Math.atan2(x, z * cl - y * sl);
          const lit = Math.max(0, x * LIGHT[0] + y * LIGHT[1] + z * LIGHT[2]);
          // land thins out as we lean in, so the route has room
          const v = isLand(la, lo) ? (0.22 + 0.85 * lit) * (1 - 0.6 * lean) : (0.02 + 0.09 * lit) * (1 - lean);
          if (v > threshold(i, j)) grid[j * CELLS + i] = 1;
        }
      }

      const cellOf = (p) => {
        const c = Math.cos(p.lon - lon);
        if (sl * Math.sin(p.lat) + cl * Math.cos(p.lat) * c < 0.05) return -1;
        const X = Math.cos(p.lat) * Math.sin(p.lon - lon) * zoom;
        const Y = (cl * Math.sin(p.lat) - sl * Math.cos(p.lat) * c) * zoom;
        if (X * X + Y * Y > 0.97) return -1;
        return Math.floor(((1 - Y) / 2) * CELLS) * CELLS + Math.floor(((X + 1) / 2) * CELLS);
      };
      const head = Math.floor(ride * (r.trail.length - 1));
      if (ride > 0) {
        for (let k = 0; k < head; k++) {
          const c = cellOf(r.trail[k]);
          if (c >= 0) grid[c] = 1;
        }
        const c = cellOf(r.trail[head]);
        if (c >= 0) grid[c] = still || Math.floor(now / 400) % 2 ? 1 : 0;
      }

      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.fillStyle = colorRef.current;
      for (let j = 0; j < CELLS; j++) {
        for (let i = 0; i < CELLS; i++) {
          // Bayer dissolve: cells hand over from the sphere in matrix order
          if (grid[j * CELLS + i] && threshold(i, j) < shown) ctx.fillRect(i * px, j * px, px, px);
        }
      }
      raf.current = requestAnimationFrame(frame);
    };
    raf.current = requestAnimationFrame(frame);
  }, [route]);

  return <canvas ref={ref} className="globe" style={{ width: size, height: size }} aria-hidden />;
}
